import { useState, useCallback, useRef, useEffect } from 'react';
import { forceSessionRefresh, resetSessionState } from '@/services/auth/sessionUtils';
import { createLogger } from '@/services/utils/loggingUtils';
import { Session } from '@supabase/supabase-js';

const logger = createLogger('useSessionRefresher');
const REFRESH_INTERVAL = 60 * 60 * 1000; // 60 minutes (increased from 30 minutes)
const MAX_REFRESH_FAILURES = 3;

// Keep track of refreshes across hook instances
const globalRefreshState = {
  lastRefreshTime: 0,
  inProgress: false
};

export const useSessionRefresher = (initialSession: Session | null) => {
  const [refreshErrors, setRefreshErrors] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(0);
  const [session, setSession] = useState<Session | null>(initialSession);
  const previousSessionRef = useRef<Session | null>(initialSession);
  const firstLoadCompleted = useRef(false);
  const refreshIntervalId = useRef<number | null>(null);
  const componentMounted = useRef(true);

  // Update session when initialSession changes, but avoid unnecessary state updates
  useEffect(() => {
    // Only update if there's an actual change and not during initial setup
    if (initialSession !== previousSessionRef.current) {
      if (firstLoadCompleted.current) {
        logger.debug(`Initial session changed, updating state`);
      }
      previousSessionRef.current = initialSession;
      setSession(initialSession);
      firstLoadCompleted.current = true;
    }
  }, [initialSession]);

  // Force refresh the session with debouncing
  const refreshSession = useCallback(async (): Promise<boolean> => {
    const now = Date.now();
    
    // Use global refresh state to prevent multiple refreshes across hooks
    if (globalRefreshState.inProgress) {
      logger.debug('Global refresh already in progress, skipping');
      return !!session;
    }
    
    // Apply rate limiting to prevent excessive refresh
    if (now - globalRefreshState.lastRefreshTime < 60000) { // 60 seconds minimum between refreshes
      logger.debug('Global refresh attempted too soon, skipping');
      return !!session;
    }
    
    if (now - lastRefresh < 30000) { // 30 seconds minimum between refreshes in this instance
      logger.debug('Local refresh attempted too soon, skipping');
      return !!session;
    }
    
    globalRefreshState.inProgress = true;
    setLastRefresh(now);
    globalRefreshState.lastRefreshTime = now;
    
    try {
      logger.debug('Refreshing authentication session');
      
      resetSessionState();
      const newSession = await forceSessionRefresh();
      
      if (newSession) {
        setRefreshErrors(0);
        
        // Only update session state if it's actually different
        // Using JSON.stringify for deep comparison
        const currentSessionStr = JSON.stringify(previousSessionRef.current);
        const newSessionStr = JSON.stringify(newSession);
        
        if (currentSessionStr !== newSessionStr) {
          logger.debug('Session content changed during refresh, updating state');
          if (componentMounted.current) {
            previousSessionRef.current = newSession;
            setSession(newSession);
          }
        } else {
          logger.debug('Session unchanged after refresh, skipping state update');
        }
        
        globalRefreshState.inProgress = false;
        return true;
      } else {
        const errors = refreshErrors + 1;
        if (componentMounted.current) {
          setRefreshErrors(errors);
        }
        globalRefreshState.inProgress = false;
        
        if (errors >= MAX_REFRESH_FAILURES) {
          logger.error(`Maximum refresh failures (${errors}) reached`);
          return false;
        }
        
        return false;
      }
    } catch (error) {
      logger.error('Error refreshing session:', error);
      if (componentMounted.current) {
        setRefreshErrors(prev => prev + 1);
      }
      globalRefreshState.inProgress = false;
      return false;
    }
  }, [session, refreshErrors, lastRefresh]);

  // Periodic session refresh - using interval ref to avoid dependency issues
  useEffect(() => {
    if (!session) return;
    
    // Skip periodic refresh setup during initial render
    if (!firstLoadCompleted.current) {
      firstLoadCompleted.current = true;
      return;
    }
    
    // Clean up existing interval if it exists before creating a new one
    if (refreshIntervalId.current) {
      clearInterval(refreshIntervalId.current);
    }
    
    logger.debug('Setting up periodic session refresh');
    
    // Use adaptive refresh interval based on page visibility
    const setRefreshInterval = () => {
      if (refreshIntervalId.current) {
        clearInterval(refreshIntervalId.current);
      }
      
      const interval = document.visibilityState === 'visible' ? REFRESH_INTERVAL : REFRESH_INTERVAL * 2;
      
      refreshIntervalId.current = window.setInterval(() => {
        if (session && !globalRefreshState.inProgress && componentMounted.current) {
          refreshSession().catch(err => {
            console.error("Error in scheduled session refresh:", err);
          });
        }
      }, interval);
    };
    
    // Set initial interval
    setRefreshInterval();
    
    // Update interval when visibility changes
    document.addEventListener('visibilitychange', setRefreshInterval);
    
    return () => {
      logger.debug('Cleaning up session refresh interval');
      document.removeEventListener('visibilitychange', setRefreshInterval);
      if (refreshIntervalId.current) {
        clearInterval(refreshIntervalId.current);
        refreshIntervalId.current = null;
      }
    };
  }, [session, refreshSession]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      componentMounted.current = false;
    };
  }, []);

  return {
    refreshErrors,
    refreshSession,
    session
  };
};
